import { useState } from 'react';
import { Plus } from 'lucide-react';
import { ROLE_LABELS, projectsApi, usersApi, type Project, type Role, type User } from '../api';
import { useAuth } from '../auth';
import { useAsync } from '../hooks';
import { Alert, Badge, Button, Checkbox, Field, Modal, PageHeader, Select, Table, errorText, timeAgo } from '../components/ui';

export default function UsersPage() {
  const { user: me } = useAuth();
  const users = useAsync(usersApi.list, []);
  const projects = useAsync(projectsApi.list, []);
  const [editing, setEditing] = useState<User | 'new' | null>(null);

  return (
    <div>
      <PageHeader title="Users">
        <Button onClick={() => setEditing('new')}>
          <Plus size={16} /> New user
        </Button>
      </PageHeader>
      {users.error && <Alert kind="error">{users.error}</Alert>}
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Role</th>
            <th>Projects</th>
            <th>Status</th>
            <th>Last login</th>
          </tr>
        </thead>
        <tbody>
          {(users.data ?? []).map((u) => (
            <tr key={u.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setEditing(u)}>
              <td className="font-medium">
                {u.name || u.username}
                {u.id === me?.id && <span className="ml-1 text-xs text-slate-400">(you)</span>}
              </td>
              <td className="font-mono text-xs text-slate-600">{u.username}</td>
              <td>{ROLE_LABELS[u.role]}</td>
              <td>{u.role === 'admin' ? <span className="text-slate-400">all</span> : u.projects.map((p) => p.name).join(', ') || '—'}</td>
              <td>{u.is_active ? <Badge tone="green">active</Badge> : <Badge>inactive</Badge>}</td>
              <td className="text-slate-500">{timeAgo(u.last_login_at)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {editing && (
        <UserModal
          user={editing === 'new' ? null : editing}
          isSelf={editing !== 'new' && editing.id === me?.id}
          projects={projects.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            users.reload();
          }}
        />
      )}
    </div>
  );
}

function UserModal({
  user,
  isSelf,
  projects,
  onClose,
  onSaved,
}: {
  user: User | null;
  isSelf: boolean;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [username, setUsername] = useState(user?.username ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'release_manager');
  const [active, setActive] = useState(user?.is_active ?? true);
  const [password, setPassword] = useState('');
  const [projectIds, setProjectIds] = useState(new Set(user?.projects.map((p) => p.id) ?? []));
  const [error, setError] = useState<string | null>(null);

  const toggleProject = (id: number) => {
    const next = new Set(projectIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setProjectIds(next);
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      onSaved();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const save = () =>
    run(() =>
      user
        ? usersApi.update(user.id, {
            name: name.trim(),
            role,
            is_active: active,
            project_ids: [...projectIds],
            ...(password ? { password } : {}),
          })
        : usersApi.create({ username, name: name.trim(), password, role, project_ids: [...projectIds] }),
    );

  return (
    <Modal title={user ? `Edit ${user.name || user.username}` : 'New user'} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Name" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jana Nováková" />
        <Field
          label="Username (for sign-in)"
          value={username}
          disabled={!!user}
          onChange={(e) => setUsername(e.target.value)}
          hint={user ? undefined : "3–64 characters: letters, digits, '.', '_', '-'"}
        />
        <Select label="Role" value={role} disabled={isSelf} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="release_manager">Release Manager</option>
          <option value="admin">Admin</option>
        </Select>
        {role === 'release_manager' && (
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Projects</span>
            {projects.length === 0 ? (
              <p className="text-sm text-slate-500">No projects yet.</p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {projects.map((p) => (
                  <Checkbox key={p.id} label={p.name} checked={projectIds.has(p.id)} onChange={() => toggleProject(p.id)} />
                ))}
              </div>
            )}
          </div>
        )}
        <Field
          label={user ? 'New password (leave empty to keep)' : 'Password'}
          type="password"
          autoComplete="new-password"
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 10 characters."
        />
        {user && <Checkbox label="Active (can sign in)" checked={active} disabled={isSelf} onChange={(e) => setActive(e.target.checked)} />}
        <div className="flex flex-wrap justify-between gap-2">
          {user && !isSelf ? (
            <Button variant="danger" onClick={() => confirm(`Delete user ${user.name || user.username}?`) && run(() => usersApi.remove(user.id))}>
              Delete user
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save} disabled={!name.trim() || (!user && (!username || password.length < 10))}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
